import { motion } from "framer-motion";
import { AlertCircle, AlertTriangle, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export const inputClass =
  "block w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 text-[15px] text-white placeholder:text-[#8E8E98] outline-none transition-[border-color,box-shadow,background-color] duration-200 hover:border-white/20 focus:border-[#2F6BFF] focus:bg-white/[0.05] focus:shadow-[0_0_0_4px_rgba(47,107,255,0.2)] aria-[invalid=true]:border-[#F87171]/70";

export const a11y = (id, error, required) => ({
  id,
  "aria-invalid": error ? true : undefined,
  "aria-describedby": error ? `${id}-error` : undefined,
  "aria-required": required || undefined,
});

export const Field = ({ id, label, required, optional, error, className, children }) => (
  <div className={cn("space-y-2", className)}>
    <label htmlFor={id} className="flex items-baseline justify-between text-sm font-medium text-white/90">
      <span>
        {label}
        {required && (
          <span className="text-[#2DD4D4]" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </span>
      {optional && <span className="text-xs font-normal text-[#A1A1AA]">Optional</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} className="flex items-center gap-1.5 text-sm text-[#FCA5A5]" data-testid={`${id}-error`}>
        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {error}
      </p>
    )}
  </div>
);

export const ErrorAlert = ({ message, testId }) => (
  <div
    role="alert"
    data-testid={testId}
    className="flex items-start gap-3 rounded-lg border border-[#F87171]/30 bg-[#F87171]/10 px-4 py-3 text-sm text-[#FECACA]"
  >
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    <span>{message}</span>
  </div>
);

export const SuccessPanel = ({ title, body, onReset, resetLabel, testId }) => (
  <motion.div
    role="status"
    aria-live="polite"
    data-testid={testId}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="flex flex-col items-start py-4"
  >
    <span className="grid h-12 w-12 place-items-center rounded-full border border-[#2DD4D4]/30 bg-[#2DD4D4]/10">
      <Check className="h-5 w-5 text-[#2DD4D4]" aria-hidden="true" />
    </span>
    <h3 className="mt-6 text-2xl font-semibold tracking-tight">{title}</h3>
    <p className="mt-3 max-w-md leading-relaxed text-[#A1A1AA]">{body}</p>
    <button
      type="button"
      onClick={onReset}
      data-testid={`${testId}-reset-button`}
      className="mt-8 text-sm font-medium text-white underline-offset-4 transition-colors hover:text-[#2DD4D4] hover:underline"
    >
      {resetLabel}
    </button>
  </motion.div>
);

export const Honeypot = ({ value, onChange }) => (
  <div className="hp-field" aria-hidden="true">
    <label>
      Leave this field empty
      <input type="text" name="fax" tabIndex={-1} autoComplete="off" value={value} onChange={onChange} />
    </label>
  </div>
);
